<template lang="pug">
    layout.contentTransparent(fluid)

        // Title
        title-component(
            :title="$t('activityReport.title')"
            slot="title"
            type="h2"
        )

        div.activityReport
            p.description {{ $t('activityReport.description') }}

            // Filters
            v-layout.filters.c-form(row wrap align-center)
                v-flex(xs12 sm6 md4 lg3)
                    v-select.filterField(
                        v-model="businessId"
                        :label="$t('activityReport.filters.business')"
                        :items="businessItems"
                        :disabled="isLoading"
                        name="businessId"
                        outline
                        hide-details
                    )

                v-flex(xs6 sm3 md2)
                    v-menu(
                        v-model="menuFrom"
                        :close-on-content-click="false"
                        lazy
                        offset-y
                        full-width
                        max-width="290px"
                    )
                        template(v-slot:activator="{ on }")
                            v-text-field.filterField(
                                v-on="on"
                                :label="$t('activityReport.filters.dateFrom')"
                                :value="filter.dateFrom"
                                :disabled="isLoading"
                                name="dateFrom"
                                append-icon="event"
                                readonly
                                outline
                                hide-details
                            )
                        v-date-picker(
                            :value="filter.dateFrom"
                            :max="filter.dateTo || today"
                            color="primary"
                            no-title
                            scrollable
                            @input="onDateFrom"
                        )

                v-flex(xs6 sm3 md2)
                    v-menu(
                        v-model="menuTo"
                        :close-on-content-click="false"
                        lazy
                        offset-y
                        full-width
                        max-width="290px"
                    )
                        template(v-slot:activator="{ on }")
                            v-text-field.filterField(
                                v-on="on"
                                :label="$t('activityReport.filters.dateTo')"
                                :value="filter.dateTo"
                                :disabled="isLoading"
                                name="dateTo"
                                append-icon="event"
                                readonly
                                outline
                                hide-details
                            )
                        v-date-picker(
                            :value="filter.dateTo"
                            :min="filter.dateFrom"
                            :max="today"
                            color="primary"
                            no-title
                            scrollable
                            @input="onDateTo"
                        )

                v-flex(xs12 md4 lg5)
                    div.generateWrapper
                        button-component.generateButton.mx-0(
                            :action="fetchReport"
                            :isLoading="isLoading"
                            :disabled="isLoading || !isRangeValid"
                            :outline="!isStale && hasReport"
                            color="action"
                            large
                        ) {{ $t('activityReport.actions.generate') }}
                        div.staleHint(v-if="isStale") {{ $t('activityReport.stale') }}

            div.toolbar
                // Quick presets
                div.presets
                    v-chip.preset(
                        v-for="preset in presets"
                        :key="preset"
                        :data-preset="preset"
                        :class="{ isActive: preset === activePreset }"
                        :disabled="isLoading"
                        color="white"
                        outline
                        small
                        @click="onPreset(preset)"
                    ) {{ $t(`activityReport.presets.${preset}`) }}

                // All-time list of members who never logged in
                div.neverLoggedIn
                    div.neverLoggedInHint {{ $t('activityReport.neverLoggedIn.hint') }}
                    button-component.neverLoggedInButton.mx-0(
                        :action="downloadNeverLoggedIn"
                        :isLoading="isNeverLoggedInLoading"
                        :disabled="isNeverLoggedInLoading"
                        color="action"
                        outline
                        medium
                    ) {{ $t('activityReport.neverLoggedIn.action') }}

            div.errorMessage(v-if="errorMessage" role="alert") {{ errorMessage }}

            v-progress-linear.loader(
                v-if="isLoading"
                color="action"
                height="2"
                indeterminate
            )

            separator-component

            template(v-if="hasReport")

                // Headline numbers
                v-layout.summary(row wrap)
                    v-flex(
                        v-for="card in summaryCards"
                        :key="card.key"
                        xs12 sm6 md3
                    )
                        summary-card(
                            :label="card.label"
                            :value="card.value"
                            :sub="card.sub"
                            :hint="card.hint"
                            :muted="card.muted"
                        )

                // Nothing happened in this range
                div.noActivity(v-if="!hasActivity" role="status")
                    v-icon.mr-2(color="white") info_outline
                    span {{ $t('activityReport.noActivity', { scope: scopeLabel, from: formatDate(report.dateFrom), to: formatDate(report.dateTo) }) }}

                // Daily trend
                div.chartCard(v-else)
                    h3.sectionTitle {{ $t('activityReport.chart.title') }}
                    div.chartWrapper
                        trend-chart.chart(
                            :daily="daily"
                            :dates="chartDates"
                            :labels="chartLabels"
                        )

                // Table header + exports
                v-layout.tableHeader(row wrap justify-space-between align-center)
                    v-flex(xs12 md5)
                        h3.sectionTitle {{ $t('activityReport.table.title') }}
                        div.tableScope {{ scopeLabel }} · {{ formatDate(report.dateFrom) }} – {{ formatDate(report.dateTo) }} · {{ $t('activityReport.generatedAt', { time: generatedAt }) }}

                    v-flex(xs12 md7)
                        div.exports
                            v-switch.hideEmpty(
                                v-model="hideEmptyDays"
                                :label="$t('activityReport.table.hideEmptyDays')"
                                color="action"
                                hide-details
                            )
                            button-component.exportCsv(
                                :action="downloadCsv"
                                :disabled="isLoading"
                                color="action"
                                outline
                                medium
                            ) {{ $t('activityReport.actions.exportCsv') }}
                            button-component.exportPdf(
                                :action="downloadPdf"
                                :disabled="isLoading"
                                color="action"
                                medium
                            ) {{ $t('activityReport.actions.exportPdf') }}

                div.hiddenNote(v-if="hideEmptyDays && hiddenDays > 0") {{ $t('activityReport.table.emptyDaysHidden', { count: hiddenDays }) }}

                // Daily breakdown
                v-data-table.c-table.visibleHeader(
                    :headers="headers"
                    :items="visibleDaily"
                    :rows-per-page-items="rowsPerPageItems"
                    :rows-per-page-text="$t('activityReport.table.rowsPerPage')"
                    :no-data-text="$t('activityReport.table.allDaysEmpty')"
                    item-key="date"
                )
                    template(v-slot:items="props")
                        tr(:class="{ peak: peak && props.item.date === peak.date }")
                            td {{ props.item.date | dayWithWeekday }}
                            td.text-xs-right {{ props.item.uniqueLogins | numberWithCommas }}
                            td.text-xs-right {{ props.item.uniqueMeditated | numberWithCommas }}

            div.empty(v-else-if="!isLoading") {{ $t('activityReport.empty') }}
</template>

<script>
    import dayjs from 'dayjs';
    import { mapActions, mapGetters } from 'vuex';
    import Layout from '../Layout/Layout';
    import Button from '~components/Button';
    import Separator from '~components/Separator';
    import Title from '~components/Title';
    import SummaryCard from './SummaryCard';
    import TrendChart from './TrendChart';
    import formattedDate from '~filters/FormattedDate';
    import numberWithCommas from '~filters/numberWithCommas';
    import { ALL_BUSINESSES, PRESETS } from '~/models/ActivityReport';
    import {
        exportCsv,
        exportNeverLoggedInCsv,
        exportPdf,
        formatRate
    } from './export';
    import { hasActivity, peakDay } from './chart-data';

    export default {
        components: {
            Layout,
            SummaryCard,
            TrendChart,
            TitleComponent: Title,
            ButtonComponent: Button,
            SeparatorComponent: Separator
        },
        filters: {
            numberWithCommas,
            dayWithWeekday: value => dayjs(value).format('ddd, MM/DD/YYYY')
        },
        data() {
            return {
                menuFrom: false,
                menuTo: false,
                hideEmptyDays: true,
                today: dayjs().format('YYYY-MM-DD'),
                presets: Object.keys(PRESETS),
                rowsPerPageItems: [
                    31,
                    92,
                    { text: this.$t('activityReport.table.all'), value: -1 }
                ]
            };
        },
        computed: {
            ...mapGetters('activityReport', [
                'filter',
                'report',
                'isLoading',
                'hasReport',
                'isRangeValid',
                'daily',
                'totals',
                'error',
                'isNeverLoggedInLoading'
            ]),
            ...mapGetters('businesses', ['businesses']),
            businessId: {
                get() {
                    return this.filter.businessId;
                },
                set(value) {
                    this.setFilter({ businessId: value });
                }
            },
            businessItems() {
                return [
                    {
                        text: this.$t('activityReport.filters.allBusinesses'),
                        value: ALL_BUSINESSES
                    },
                    ...this.businesses.map(item => ({
                        text: item.title,
                        value: item.value
                    }))
                ];
            },
            /** Preset whose dates equal the current filter, if any. */
            activePreset() {
                const today = dayjs();
                return (
                    this.presets.find(key => {
                        const range = PRESETS[key](today);
                        return (
                            range.dateFrom === this.filter.dateFrom &&
                            range.dateTo === this.filter.dateTo
                        );
                    }) || null
                );
            },
            /** Filters differ from what the visible report was generated with. */
            isStale() {
                if (!this.report) return false;
                const reportBusiness = this.report.business
                    ? this.report.business.id
                    : ALL_BUSINESSES;
                return (
                    reportBusiness !== this.filter.businessId ||
                    this.report.dateFrom !== this.filter.dateFrom ||
                    this.report.dateTo !== this.filter.dateTo
                );
            },
            hasActivity() {
                return hasActivity(this.daily);
            },
            peak() {
                return peakDay(this.daily);
            },
            visibleDaily() {
                if (!this.hideEmptyDays) return this.daily;
                return this.daily.filter(
                    day => day.uniqueLogins > 0 || day.uniqueMeditated > 0
                );
            },
            hiddenDays() {
                return this.daily.length - this.visibleDaily.length;
            },
            chartDates() {
                return this.daily.map(day => this.formatDate(day.date));
            },
            chartLabels() {
                return {
                    logins: this.$t('activityReport.table.uniqueLogins'),
                    meditated: this.$t('activityReport.table.uniqueMeditated')
                };
            },
            generatedAt() {
                return dayjs(this.report.generatedAt).format(
                    'MM/DD/YYYY HH:mm'
                );
            },
            headers() {
                return [
                    {
                        text: this.$t('activityReport.table.date'),
                        value: 'date',
                        align: 'left'
                    },
                    {
                        text: this.$t('activityReport.table.uniqueLogins'),
                        value: 'uniqueLogins',
                        align: 'right'
                    },
                    {
                        text: this.$t('activityReport.table.uniqueMeditated'),
                        value: 'uniqueMeditated',
                        align: 'right'
                    }
                ];
            },
            scopeLabel() {
                return this.report && this.report.business
                    ? this.report.business.name
                    : this.$t('activityReport.filters.allBusinesses');
            },
            summaryCards() {
                if (!this.totals) return [];

                const t = (key, params) =>
                    this.$t(`activityReport.summary.${key}`, params);
                const { members, uniqueLogins, uniqueMeditated } = this.totals;
                const ofMembers = count =>
                    members
                        ? t('ofMembers', { rate: formatRate(count / members) })
                        : '';
                const cards = [];

                if (members !== null) {
                    cards.push({
                        key: 'members',
                        label: t('members'),
                        value: numberWithCommas(members),
                        hint: t('membersHint')
                    });
                }

                cards.push(
                    {
                        key: 'uniqueLogins',
                        label: t('uniqueLogins'),
                        value: numberWithCommas(uniqueLogins),
                        sub: ofMembers(uniqueLogins),
                        hint: t('uniqueLoginsHint'),
                        muted: uniqueLogins === 0
                    },
                    {
                        key: 'uniqueMeditated',
                        label: t('uniqueMeditated'),
                        value: numberWithCommas(uniqueMeditated),
                        sub: ofMembers(uniqueMeditated),
                        hint: t('uniqueMeditatedHint'),
                        muted: uniqueMeditated === 0
                    }
                );

                if (uniqueLogins > 0) {
                    cards.push({
                        key: 'meditatedRate',
                        label: t('meditatedRate'),
                        value: formatRate(uniqueMeditated / uniqueLogins),
                        hint: t('meditatedRateHint')
                    });
                } else {
                    cards.push({
                        key: 'meditatedRate',
                        label: t('meditatedRate'),
                        value: '—',
                        hint: t('noLogins'),
                        muted: true
                    });
                }

                // Platform-wide reports have no member count: use the free
                // slot for the busiest day instead.
                if (members === null) {
                    cards.unshift(
                        this.peak
                            ? {
                                  key: 'peakDay',
                                  label: t('peakDay'),
                                  value: this.formatDate(this.peak.date),
                                  hint: t('peakDayHint', {
                                      count: numberWithCommas(
                                          this.peak.uniqueLogins
                                      )
                                  })
                              }
                            : {
                                  key: 'peakDay',
                                  label: t('peakDay'),
                                  value: '—',
                                  hint: t('noPeak'),
                                  muted: true
                              }
                    );
                }

                return cards;
            },
            errorMessage() {
                if (!this.error) return '';
                return this.$t(`activityReport.errors.${this.error}`);
            }
        },
        created() {
            if (!this.businesses.length) {
                this.getterBusinessesWithDepartments();
            }
            if (!this.hasReport) {
                this.fetchReport();
            }
        },
        methods: {
            ...mapActions('activityReport', [
                'setFilter',
                'applyPreset',
                'fetchReport',
                'fetchNeverLoggedIn'
            ]),
            ...mapActions('businesses', ['getterBusinessesWithDepartments']),
            ...mapActions('snackbar', ['showMessage']),
            formatDate: formattedDate,
            onDateFrom(value) {
                this.setFilter({ dateFrom: value });
                this.menuFrom = false;
            },
            onDateTo(value) {
                this.setFilter({ dateTo: value });
                this.menuTo = false;
            },
            async onPreset(key) {
                await this.applyPreset(key);
                return this.fetchReport();
            },
            downloadCsv() {
                if (this.report) exportCsv(this.report);
            },
            downloadPdf() {
                if (this.report) exportPdf(this.report);
            },
            /**
             * Download every member of the selected business (or platform)
             * who has never logged in. API errors are surfaced by the store.
             */
            async downloadNeverLoggedIn() {
                const payload = await this.fetchNeverLoggedIn();
                if (!payload) return;

                if (!payload.total) {
                    this.showMessage(
                        this.$t('activityReport.neverLoggedIn.noneFound')
                    );
                    return;
                }

                exportNeverLoggedInCsv(payload);
                this.showMessage(
                    this.$tc(
                        'activityReport.neverLoggedIn.downloaded',
                        payload.total,
                        { count: numberWithCommas(payload.total) }
                    )
                );
            }
        }
    };
</script>

<style lang="stylus" scoped>
    @import "../../../assets/style/_variables.styl";

    .activityReport
        color: $theme.white

    .description
        max-width: 760px
        margin: 0 0 20px
        font-size: 14px
        opacity: 0.85

    .filters
        .filterField
            margin-right: 12px

    .generateWrapper
        display: flex
        align-items: center
        flex-wrap: wrap
        height: 100%

    .staleHint
        margin-left: 12px
        font-size: 13px
        color: $theme.warning

    .toolbar
        display: flex
        align-items: center
        justify-content: space-between
        flex-wrap: wrap
        margin: 16px 0 12px

    .presets
        margin-right: 16px

        .preset
            cursor: pointer
            transition: background-color 0.2s

            &.isActive
                background-color: $theme.action !important
                border-color: $theme.action !important
                color: $theme.white !important

    .neverLoggedIn
        display: flex
        align-items: center
        margin-left: auto

    .neverLoggedInHint
        max-width: 300px
        margin-right: 16px
        font-size: 12px
        text-align: right
        opacity: 0.75

    .loader
        margin: 0

    .errorMessage
        margin: 8px 0
        padding: 10px 14px
        border-radius: 10px
        background: rgba($theme.error, 0.18)
        color: $theme.white
        font-size: 14px

    .summary
        margin: 8px 0 24px

        .flex
            padding: 8px !important

    .noActivity
        display: flex
        align-items: center
        margin: 0 0 28px
        padding: 16px 20px
        border-radius: 10px
        background: rgba(255, 255, 255, 0.08)
        font-size: 15px

    .chartCard
        margin: 0 0 28px
        padding: 20px 20px 16px
        border-radius: 10px
        background: rgba(255, 255, 255, 0.1)
        box-shadow: 0 0 15px rgba(0, 0, 0, 0.1)

    .chartWrapper
        position: relative
        height: 280px

        .chart
            height: 100%

    .sectionTitle
        margin-bottom: 8px
        font-size: 20px
        font-weight: 700

    .tableHeader
        margin: 8px 0 16px

    .tableScope
        margin-top: 4px
        font-size: 13px
        opacity: 0.75

    .exports
        display: flex
        align-items: center
        justify-content: flex-end
        flex-wrap: wrap

        .hideEmpty
            flex: 0 0 auto
            margin: 0 24px 0 0
            padding: 0

    .hiddenNote
        margin: 0 0 8px
        font-size: 13px
        opacity: 0.7

    .empty
        padding: 24px 0
        text-align: center
        font-size: 15px
        opacity: 0.8

    @media screen and (max-width: $grid-breakpoints.md - 1)
        .exports
            justify-content: flex-start

            .hideEmpty
                flex-basis: 100%
                margin-bottom: 8px

        .filters
            .filterField
                margin-right: 0

        .neverLoggedIn
            flex-direction: column
            align-items: flex-start
            margin: 12px 0 0

        .neverLoggedInHint
            max-width: none
            margin: 0 0 4px
            text-align: left

        .chartWrapper
            height: 220px
</style>

<style lang="stylus">
    @import "../../../assets/style/_variables.styl";

    // Highlight the busiest day in the breakdown table.
    .activityReport .c-table tbody > tr.peak td
        font-weight: 700
        color: $theme.action

    // v-switch label inherits Vuetify's dark-on-light default; the page is dark.
    .activityReport .hideEmpty .v-label
        color: $theme.white
        font-size: 14px
</style>
